
import { useState, useEffect } from "react";
import { parsePrompt } from "@/utils/promptParser";
import MeetingPrompt from "@/components/meeting/MeetingPrompt";
import Header from "@/components/meeting/Header";
import ChatHistory from "@/components/meeting/ChatHistory";
import { handleGoogleSignIn } from "@/components/auth/AuthHandler";
import { useToast } from "@/hooks/use-toast";
import { safeRemoveItem, safeSetItem } from "@/utils/storage";

interface ChatMessage {
  type: 'user' | 'ai';
  content: string;
  id: number;
}

interface MainContentProps {
  isAuthenticated: boolean;
  userEmail: string | null;
  isLoading: boolean;
  prompt: string;
  feedback?: string;
  onPromptChange: (value: string) => void;
  onSignOut: () => void;
  onGoogleSignIn: () => void;
  onSchedule: () => void;
  parsedMeeting?: {
    dateTime: Date | null;
    attendeeName: string | null;
    duration?: number;
  } | null;
}

const MainContent = ({
  isAuthenticated,
  userEmail,
  isLoading,
  prompt,
  feedback,
  onPromptChange,
  onSignOut,
  onGoogleSignIn,
  onSchedule,
  parsedMeeting
}: MainContentProps) => {
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [messageIdCounter, setMessageIdCounter] = useState(0);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastProcessedFeedback, setLastProcessedFeedback] = useState<string | null>(null);
  const [isReadyToSchedule, setIsReadyToSchedule] = useState(false);
  const { toast } = useToast();

  const addMessage = (type: 'user' | 'ai', content: string) => {
    setChatHistory(prev => {
      const isDuplicate = prev.some(msg => 
        msg.type === type && msg.content === content
      );
      
      if (isDuplicate) return prev;
      
      return [...prev, { 
        type, 
        content,
        id: messageIdCounter
      }];
    });
    setMessageIdCounter(prev => prev + 1);
  };

  useEffect(() => {
    if (feedback && !isLoading && feedback !== lastProcessedFeedback) {
      addMessage('ai', feedback);
      setLastProcessedFeedback(feedback);
      
      const isReady = feedback.includes("Sign in to schedule") || 
                     feedback.includes("Click Schedule to confirm");
      setIsReadyToSchedule(isReady);
    }
  }, [feedback, isLoading, lastProcessedFeedback]);

  useEffect(() => {
    if (chatHistory.length > 0) {
      setIsHeaderVisible(false);
    }
  }, [chatHistory]);

  const hasChatStarted = chatHistory.length > 0;

  const handleSubmit = () => {
    if (prompt.trim()) {
      addMessage('user', prompt.trim());
      onSchedule();
    }
  };

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      <div className="w-full max-w-5xl mx-auto px-4 flex flex-col h-full overflow-hidden">
        <div className="pt-8">
          <Header
            isVisible={isHeaderVisible}
            isAuthenticated={isAuthenticated}
            userEmail={userEmail}
            onToggleVisibility={() => setIsHeaderVisible(!isHeaderVisible)}
            onSignOut={onSignOut}
          />
        </div>

        <main className="flex-1 flex flex-col overflow-hidden justify-between">
          <div className={`${hasChatStarted ? 'flex-1 overflow-y-auto' : 'overflow-hidden'}`}>
            <ChatHistory
              messages={chatHistory}
              isReadyToSchedule={isReadyToSchedule}
              isAuthenticated={isAuthenticated}
              isLoading={isLoading}
              parsedMeeting={parsedMeeting}
              onGoogleSignIn={onGoogleSignIn}
              onSchedule={onSchedule}
            />
          </div>

          <div className="flex-none bg-background pb-4">
            <div className="mx-auto w-full max-w-2xl px-4">
              <MeetingPrompt
                isLoading={isLoading}
                prompt={prompt}
                feedback={feedback}
                onPromptChange={onPromptChange}
                onSubmit={handleSubmit}
                isAuthenticated={isAuthenticated}
                isReadyToSchedule={isReadyToSchedule}
                parsedMeeting={parsedMeeting}
                onGoogleSignIn={onGoogleSignIn}
                onSchedule={onSchedule}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainContent;
